"use client";

import { useRef, useState } from "react";
import { devLabel } from "@/lib/dev-labels";

export interface FileUploadModeProps {
	/** Passes the actual picked File (Increment 14 — was a filename string). */
	onAnalyze: (file: File) => void;
}

interface PickedFile {
	name: string;
	meta: string;
	file: File;
}

export function FileUploadMode({ onAnalyze }: FileUploadModeProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [picked, setPicked] = useState<PickedFile | null>(null);
	const [dragOver, setDragOver] = useState(false);

	function handleFiles(files: FileList | null | undefined) {
		const f = files?.[0];
		if (!f) return;
		const mb = (f.size / (1024 * 1024)).toFixed(0);
		setPicked({
			name: f.name,
			meta: `${mb} MB · ${f.type || "video/mp4"}`,
			file: f,
		});
	}

	function reset() {
		setPicked(null);
		if (inputRef.current) inputRef.current.value = "";
	}

	return (
		<div {...devLabel("11-A3")}>
			<div
				onClick={() => inputRef.current?.click()}
				onDragOver={(e) => {
					e.preventDefault();
					setDragOver(true);
				}}
				onDragLeave={() => setDragOver(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragOver(false);
					handleFiles(e.dataTransfer.files);
				}}
				role="button"
				tabIndex={0}
				aria-label="영상 파일 끌어다 놓기"
				style={{
					padding: "18px 14px",
					border: dragOver
						? "1.5px solid #d4af37"
						: "1.5px dashed rgba(255,255,255,0.15)",
					borderRadius: 8,
					background: dragOver
						? "rgba(212,175,55,0.1)"
						: "rgba(0,0,0,0.25)",
					textAlign: "center",
					cursor: "pointer",
					transition: "all 0.15s",
				}}
			>
				<input
					ref={inputRef}
					type="file"
					accept="video/mp4,video/quicktime,video/x-matroska,video/webm,video/x-msvideo"
					style={{ display: "none" }}
					onChange={(e) => handleFiles(e.target.files)}
				/>
				{!picked ? (
					<>
						<div
							style={{
								fontSize: 13,
								fontWeight: 700,
								color: "#fff",
								marginBottom: 5,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 6,
							}}
						>
							<svg width="18" height="18" viewBox="0 0 24 24" style={{ color: "#d4af37" }} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M16 16l-4-4-4 4" />
								<line x1="12" y1="12" x2="12" y2="21" />
								<path d="M20.4 14.5A6 6 0 0 0 16 7H8a6 6 0 0 0-1.4 11.4" />
							</svg>
							영상 파일 끌어다 놓기
						</div>
						<div
							style={{
								fontSize: 10,
								opacity: 0.6,
								lineHeight: 1.55,
								fontFamily: "'Geist Mono',monospace",
							}}
						>
							또는 클릭해서 파일 선택
							<br />
							지원 형식:{" "}
							<strong style={{ color: "#fff" }}>
								mp4 · mov · mkv · webm · avi
							</strong>{" "}
							· 최대 4 GB
						</div>
					</>
				) : (
					<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
						<div style={{ fontSize: 13, fontWeight: 700, color: "#d4af37" }}>
							{picked.name}
						</div>
						<div
							style={{
								fontSize: 10,
								opacity: 0.65,
								fontFamily: "'Geist Mono',monospace",
							}}
						>
							{picked.meta}
						</div>
						<span
							onClick={(e) => {
								e.stopPropagation();
								reset();
							}}
							style={{
								fontSize: 10,
								color: "#4eb3c1",
								cursor: "pointer",
								marginTop: 3,
							}}
						>
							다른 파일 선택 ↑
						</span>
					</div>
				)}
			</div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginTop: 8,
				}}
			>
				<div
					style={{
						fontSize: 9,
						opacity: 0.5,
						fontFamily: "'Geist Mono',monospace",
					}}
				>
					로컬 분석 → Helper 또는 cloud 자동 선택
				</div>
				<button
					type="button"
					onClick={() => picked && onAnalyze(picked.file)}
					disabled={!picked}
					style={{
						padding: "6px 16px",
						background: picked ? "#fff" : "rgba(255,255,255,0.2)",
						color: picked ? "#0a0a0a" : "rgba(255,255,255,0.4)",
						borderRadius: 5,
						fontWeight: 700,
						fontSize: 12,
						border: "none",
						cursor: picked ? "pointer" : "not-allowed",
					}}
				>
					분석 (5코인)
				</button>
			</div>
		</div>
	);
}
